/**
 * Magnifier (Display > Lyrics > Magnify, experimental): my part drawn large
 * on one long line in a panel under the score, gliding with playback so the
 * music being sung passes a fixed line, with the phrase in big letters
 * underneath. It shows what the words alone cannot, such as a syllable held
 * over several notes.
 *
 * The line is a second OpenSheetMusicDisplay of the same MusicXML with only
 * that part visible, in OSMD's single horizontal staffline layout, drawn
 * once per verse that playback reaches with just that verse's words (as the
 * big words pick them, lyrics.js), so it stays one staff and one line of
 * words tall. The OSMD classes are passed in so this file has no bundle
 * import.
 *
 * Playback seconds become a position through two piecewise-linear maps:
 * seconds to score time (whole notes from the start of the score, so a
 * repeat jumps back) from the part's notes and rests in the player's
 * timeline, and score time to x from the drawn staff entries. Dragging the
 * strip runs them the other way, to the playback time to jump to.
 */
import { lyricVersesOf, pickVerse, verseNumber, passByPlaybackIndex } from './lyrics';

// OSMD draws 10 SVG units per unit of its layout at zoom 1
const UNIT_PX = 10;
// Same moment, as far as the timeline goes (seconds)
const SAME_TIME = 0.0005;
// Room kept around the drawn part (SVG units)
const PAD = 8;

const median = (values) => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

// The drawn (SVG) groups of an entry's sounding notes, to light up
const noteGroups = (entry) => (entry.graphicalVoiceEntries || [])
  .flatMap(voiceEntry => voiceEntry.notes || [])
  .filter(note => note.sourceNote && !note.sourceNote.isRest())
  .map(note => note.getSVGGElement?.())
  .filter(Boolean);

// Score time -> x points of the drawn part (with the note groups at each),
// and its bar widths
function ribbonPoints(osmd, partId) {
  const points = [];
  const barWidths = [];
  let staff = null;
  let end = null;
  for (const row of osmd.GraphicSheet?.MeasureList || []) {
    for (const measure of row || []) {
      if (!measure || String(measure.ParentStaff?.ParentInstrument?.Id) !== partId) continue;
      staff = staff || measure.ParentStaff;
      if (measure.ParentStaff !== staff) continue;
      const source = measure.parentSourceMeasure;
      const start = source.AbsoluteTimestamp.RealValue;
      const box = measure.PositionAndShape;
      barWidths.push(box.Size.width * UNIT_PX);
      (measure.staffEntries || []).forEach((entry) => {
        points.push({ abs: start + entry.relInMeasureTimestamp.RealValue, x: entry.PositionAndShape.AbsolutePosition.x * UNIT_PX, notes: noteGroups(entry) });
      });
      end = { abs: start + source.Duration.RealValue, x: (box.AbsolutePosition.x + box.Size.width) * UNIT_PX, notes: [] };
    }
  }
  if (end) points.push(end);
  points.sort((a, b) => a.abs - b.abs || a.x - b.x);
  return { points, barWidth: median(barWidths) };
}

/**
 * Keep one verse of a part's words, all on the first line of lyrics:
 * `verse` where it is sung, else what that bar has (a refrain written once).
 * Changes this sheet only; the magnifier draws its own copy.
 */
export function keepOneVerse(sheet, partId, verse) {
  const wanted = String(partId);
  const target = verseNumber(verse);
  (sheet?.SourceMeasures || []).forEach((measure) => {
    const voiceEntries = [];
    (measure.VerticalSourceStaffEntryContainers || []).forEach((container) => {
      (container.StaffEntries || []).forEach((staffEntry) => {
        if (staffEntry && String(staffEntry.ParentStaff?.ParentInstrument?.Id) === wanted) voiceEntries.push(...(staffEntry.VoiceEntries || []));
      });
    });
    const inBar = new Set(voiceEntries.flatMap(ve => lyricVersesOf(ve).map(v => verseNumber(v.verse))));
    voiceEntries.forEach((voiceEntry) => {
      const dict = voiceEntry.LyricsEntries;
      if (!dict || typeof dict.keys !== 'function' || dict.size() === 0) return;
      const chosen = pickVerse(lyricVersesOf(voiceEntry), target, inBar);
      dict.keys().forEach(key => dict.remove(key));
      if (!chosen) return;
      chosen.verseNumber = '1';
      dict.setValue('1', chosen);
    });
  });
  const instrument = (sheet?.Instruments || []).find(i => String(i.Id) === wanted);
  if (instrument && instrument.LyricVersesNumbers?.length) instrument.LyricVersesNumbers = ['1'];
}

/**
 * Draw one part on a single long line, off screen, with only `verse`'s
 * words (null: as printed). Resolves to { svg, points, barWidth, box } with
 * the <svg> detached, ready to be put where it is shown; `box` is the drawn
 * area in SVG units.
 */
export async function drawRibbon({ OSMD, TransposeCalculator, xml, partId, transpose = 0, verse = null }) {
  const host = document.createElement('div');
  Object.assign(host.style, { position: 'absolute', left: '-10000px', top: '0', width: '1000px', visibility: 'hidden' });
  document.body.appendChild(host);
  try {
    const osmd = new OSMD(host, {
      backend: 'svg',
      autoResize: false,
      drawTitle: false,
      drawSubtitle: false,
      drawComposer: false,
      drawLyricist: false,
      drawCredits: false,
      drawPartNames: false,
      drawPartAbbreviations: false,
      // A tempo mark at the start would make the whole strip taller
      drawMetronomeMarks: false,
      renderSingleHorizontalStaffline: true
    });
    osmd.TransposeCalculator = new TransposeCalculator();
    await osmd.load(xml);
    const wanted = String(partId);
    osmd.Sheet.Instruments.forEach((instrument) => { instrument.Visible = String(instrument.Id) === wanted; });
    if (verse !== null && verse !== undefined) keepOneVerse(osmd.Sheet, wanted, verse);
    if (transpose) osmd.Sheet.Transpose = transpose;
    osmd.zoom = 1;
    osmd.updateGraphic();
    osmd.render();
    const svg = host.querySelector('svg');
    if (!svg) throw new Error('nothing drawn');
    const { points, barWidth } = ribbonPoints(osmd, wanted);
    const drawn = svg.getBBox();
    const box = { x: drawn.x - PAD, y: drawn.y - PAD, width: drawn.width + 2 * PAD, height: drawn.height + 2 * PAD };
    svg.remove();
    return { svg, points, barWidth: barWidth || box.width, box };
  } finally {
    host.remove();
  }
}

/**
 * The part's onsets in the player's timeline, one per moment:
 * [{ t, end, abs, len, pass }] with `abs` and `len` in whole notes and
 * `pass` the time through a repeated bar.
 */
export function buildScoreClock(events, partId) {
  if (!Array.isArray(events) || partId === null || partId === undefined) return [];
  const wanted = String(partId);
  const passes = passByPlaybackIndex(events);
  const onsets = [];
  events
    .filter(e => String(e.partId) === wanted && e.sourceNote?.ParentStaffEntry?.AbsoluteTimestamp)
    .sort((a, b) => a.startTime - b.startTime)
    .forEach((e) => {
      const last = onsets[onsets.length - 1];
      if (last && Math.abs(last.t - e.startTime) < SAME_TIME) return;
      onsets.push({
        t: e.startTime,
        end: e.endTime,
        abs: e.sourceNote.ParentStaffEntry.AbsoluteTimestamp.RealValue,
        len: e.sourceNote.Length?.RealValue || 0,
        pass: passes.get(e.playbackIndex) || 1
      });
    });
  return onsets;
}

/**
 * Which verse's drawing to show for an onset: the picked verse, else the
 * pass through the bar, up to the part's last verse (null: as printed, for
 * a part without words).
 */
export function verseFor(onset, partVerses, verseSetting) {
  if (!partVerses || partVerses.length === 0) return null;
  if (verseSetting !== 'auto') return verseNumber(verseSetting);
  const last = Math.max(1, ...partVerses.map(verseNumber).filter(Number.isFinite));
  return Math.min(onset ? onset.pass : 1, last);
}

// Every verse drawing playback will need, in the order it first needs them
export function versesToDraw(onsets, partVerses, verseSetting) {
  const needed = [verseFor(onsets[0], partVerses, verseSetting)];
  (onsets || []).forEach((onset) => {
    const verse = verseFor(onset, partVerses, verseSetting);
    if (!needed.includes(verse)) needed.push(verse);
  });
  return needed;
}

// The onset sounding at `t` (the last one started), or null
export function onsetAt(onsets, t) {
  const i = lastAtOrBefore(onsets || [], t, o => o.t);
  return i >= 0 ? onsets[i] : null;
}

const clamp01 = (v) => Math.min(1, Math.max(0, v));

// Index of the last item with key(item) <= value, or -1
function lastAtOrBefore(items, value, key) {
  let lo = 0;
  let hi = items.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (key(items[mid]) <= value) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found;
}

/**
 * Score time at playback time `t`: between two onsets in step with the
 * music; across a repeat or a jump, through the note's own length.
 */
export function scoreTimeAt(onsets, t) {
  if (!onsets || onsets.length === 0) return 0;
  const i = lastAtOrBefore(onsets, t, o => o.t);
  if (i < 0) return onsets[0].abs;
  const a = onsets[i];
  const b = onsets[i + 1];
  if (b && b.abs >= a.abs && b.abs <= a.abs + a.len + 1e-6 && b.t > a.t) {
    return a.abs + (b.abs - a.abs) * clamp01((t - a.t) / (b.t - a.t));
  }
  return a.abs + a.len * clamp01((t - a.t) / Math.max(1e-6, a.end - a.t));
}

/**
 * Smooth playback time for a frame loop, from the player's 20-30 playhead
 * reports a second. Between reports it guesses ahead at the rate the
 * reports have been moving, but only while they are moving: as Play starts
 * (and through a count-in) the reports sit still for a moment, and guessing
 * then made the strip shake. It never steps back to correct a guess that
 * ran a little ahead; a real jump back (a loop starting over) goes through.
 */
export function createGlide({ maxGuess = 0.15, jump = 0.3 } = {}) {
  let t = null;
  let at = 0;
  let rate = 1;
  let moving = false;
  let shown = null;
  return {
    get time() { return t; },
    reset() {
      rate = 1;
      moving = false;
      shown = null;
    },
    report(time, now) {
      if (time === null) {
        t = null;
        moving = false;
        shown = null;
        return;
      }
      const step = t === null ? 0 : time - t;
      if (t !== null && step > 1e-4) {
        // How fast the timeline runs against the clock (live tempo changes);
        // a seek forward says nothing about that
        const seconds = (now - at) / 1000;
        if (moving && step < 0.5 && seconds > 0.01 && seconds < 0.5) {
          rate = Math.min(4, Math.max(0.25, rate * 0.7 + (step / seconds) * 0.3));
        }
        moving = true;
      } else {
        moving = false;
      }
      t = time;
      at = now;
    },
    at(now) {
      if (t === null) return null;
      const guess = moving ? t + Math.min(maxGuess, Math.max(0, (now - at) / 1000)) * rate : t;
      if (shown !== null && guess < shown && shown - guess < jump) return shown;
      shown = guess;
      return guess;
    }
  };
}

const NO_NOTES = [];

// The drawn notes starting at score time `abs` (none for a rest)
export function notesAt(points, abs) {
  const i = lastAtOrBefore(points || [], abs + 1e-6, p => p.abs);
  return i >= 0 && Math.abs(points[i].abs - abs) < 1e-6 ? points[i].notes : NO_NOTES;
}

// Score time at x on the drawn part (the other way from ribbonXAt)
export function scoreTimeAtX(points, x) {
  if (!points || points.length === 0) return 0;
  const i = lastAtOrBefore(points, x, p => p.x);
  if (i < 0) return points[0].abs;
  const a = points[i];
  const b = points[i + 1];
  if (!b || b.x <= a.x) return a.abs;
  return a.abs + (b.abs - a.abs) * clamp01((x - a.x) / (b.x - a.x));
}

/**
 * The playback time at score time `abs`, the one nearest `near` where a
 * repeated bar plays more than once (the other way from scoreTimeAt).
 */
export function playbackTimeAt(onsets, abs, near) {
  if (!onsets || onsets.length === 0) return 0;
  let best = null;
  let closest = onsets[0];
  onsets.forEach((a, i) => {
    if (Math.abs(a.abs - abs) < Math.abs(closest.abs - abs)) closest = a;
    const b = onsets[i + 1];
    const inStep = b && b.abs >= a.abs && b.abs <= a.abs + a.len + 1e-6 && b.t > a.t;
    const endAbs = inStep ? b.abs : a.abs + a.len;
    if (abs < a.abs - 1e-9 || abs > endAbs + 1e-9) return;
    const share = (abs - a.abs) / Math.max(1e-9, endAbs - a.abs);
    const t = inStep ? a.t + (b.t - a.t) * share : a.t + (a.end - a.t) * share;
    if (best === null || Math.abs(t - near) < Math.abs(best - near)) best = t;
  });
  return best ?? closest.t;
}

// x on the drawn part (SVG units) at score time `abs`
export function ribbonXAt(points, abs) {
  if (!points || points.length === 0) return 0;
  const i = lastAtOrBefore(points, abs, p => p.abs);
  if (i < 0) return points[0].x;
  const a = points[i];
  const b = points[i + 1];
  if (!b || b.abs <= a.abs) return a.x;
  return a.x + (b.x - a.x) * clamp01((abs - a.abs) / (b.abs - a.abs));
}
